import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Eye, EyeOff, Save, X, Plane, Package,
  DollarSign, Star, Image as ImageIcon, MapPin, Clock, Users,
  ChevronLeft, Settings, LogOut, Calendar, Upload, Download, FileSpreadsheet,
  Search, GripVertical, ArrowUp, ArrowDown, ExternalLink, ClipboardList, Phone, Mail
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSimpleDashboard() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Reservations state
  const [activeTab, setActiveTab] = useState('packages'); // 'packages', 'reservations', or 'events'
  const [reservations, setReservations] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(false);
  
  // Events state
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    url: '',
    scheduled_date: '',
    scheduled_time: ''
  });
  
  // Image search states
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingImages, setSearchingImages] = useState(false);
  const [manualImageUrl, setManualImageUrl] = useState('');
  const [extractUrl, setExtractUrl] = useState('');
  const [extractingImages, setExtractingImages] = useState(false);
  const [extractedImages, setExtractedImages] = useState([]);
  const [showExtractModal, setShowExtractModal] = useState(false);

  // Check if admin is authenticated (simple localStorage check)
  useEffect(() => {
    const adminAuth = localStorage.getItem('bombazoo_admin_auth');
    if (adminAuth === 'authenticated') {
      setIsAuthenticated(true);
      fetchPackages();
    } else {
      setLoading(false);
    }
  }, []);

  const handleAdminLogin = () => {
    // Simple admin password check (in production, this should be a proper auth system)
    if (adminPassword === 'Csys250969') {
      localStorage.setItem('bombazoo_admin_auth', 'authenticated');
      setIsAuthenticated(true);
      setAuthError('');
      fetchPackages();
    } else {
      setAuthError('Contraseña incorrecta');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('bombazoo_admin_auth');
    setIsAuthenticated(false);
    setPackages([]);
  };

  // Fetch reservations
  const fetchReservations = async () => {
    setLoadingReservations(true);
    try {
      const response = await fetch(`${API}/certificates/simple-admin/reservations`, {
        headers: { 'X-Admin-Password': 'Csys250969' }
      });
      const data = await response.json();
      setReservations(data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      toast.error('Error al cargar reservaciones');
    } finally {
      setLoadingReservations(false);
    }
  };

  // Delete reservation
  const deleteReservation = async (reservationId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta reservación?')) return;
    
    try {
      const response = await fetch(`${API}/certificates/simple-admin/reservations/${reservationId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': 'Csys250969' }
      });
      
      if (response.ok) {
        toast.success('Reservación eliminada');
        fetchReservations();
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  // Update reservation status
  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      const response = await fetch(`${API}/certificates/simple-admin/reservations/${reservationId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Password': 'Csys250969' 
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success('Estado actualizado');
        fetchReservations();
      } else {
        toast.error('Error al actualizar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  // ==================== EVENTS FUNCTIONS ====================
  
  // Fetch events
  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await fetch(`${API}/certificates/simple-admin/events`, {
        headers: { 'X-Admin-Password': 'Csys250969' }
      });
      const data = await response.json();
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Error al cargar eventos');
    } finally {
      setLoadingEvents(false);
    }
  };

  // Create event
  const createEvent = async () => {
    if (!newEvent.title.trim()) {
      toast.error('El título es requerido');
      return;
    }
    
    try {
      const response = await fetch(`${API}/certificates/simple-admin/events/create`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Password': 'Csys250969' 
        },
        body: JSON.stringify(newEvent)
      });
      
      if (response.ok) {
        toast.success('Evento creado');
        setShowEventModal(false);
        setNewEvent({ title: '', description: '', url: '', scheduled_date: '', scheduled_time: '' });
        fetchEvents();
      } else {
        toast.error('Error al crear evento');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  // Update event status
  const updateEventStatus = async (eventId, newStatus) => {
    try {
      const response = await fetch(`${API}/certificates/simple-admin/events/${eventId}/status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Password': 'Csys250969' 
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        const statusLabels = { live: '🔴 EN VIVO', hidden: '👁️ Oculto', ended: '⏹️ Finalizado' };
        toast.success(`Evento: ${statusLabels[newStatus]}`);
        fetchEvents();
      } else {
        toast.error('Error al actualizar estado');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  // Delete event
  const deleteEvent = async (eventId) => {
    if (!window.confirm('¿Estás seguro de eliminar este evento?')) return;
    
    try {
      const response = await fetch(`${API}/certificates/simple-admin/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': 'Csys250969' }
      });
      
      if (response.ok) {
        toast.success('Evento eliminado');
        fetchEvents();
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const downloadTemplate = () => {
    window.open(`${API}/certificates/simple-admin/csv-template`, '_blank');
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Por favor selecciona un archivo CSV');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/certificates/simple-admin/import-csv`, {
        method: 'POST',
        headers: {
          'X-Admin-Password': 'Csys250969'
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setImportResult(data);
        if (data.created > 0) {
          toast.success(`¡${data.created} paquetes importados exitosamente!`);
          fetchPackages(); // Refresh the list
        }
      } else {
        toast.error(data.detail || 'Error al importar');
        setImportResult({ success: false, errors: [data.detail] });
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Error al importar el archivo');
      setImportResult({ success: false, errors: [error.message] });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Image search and management functions
  const searchImages = async () => {
    if (!imageSearchQuery.trim()) return;
    
    setSearchingImages(true);
    try {
      const response = await fetch(
        `${API}/certificates/simple-admin/search-images?q=${encodeURIComponent(imageSearchQuery)}&count=12`,
        {
          headers: { 'X-Admin-Password': 'Csys250969' }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.images || []);
      } else {
        toast.error('Error al buscar imágenes');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Error al buscar imágenes');
    } finally {
      setSearchingImages(false);
    }
  };

  // AUTO-COMPLETE IMAGES - Busca y agrega fotos automáticamente
  const autoCompleteImages = async () => {
    // Build search query from package info
    const searchTerms = [
      editingPackage.title,
      editingPackage.destination,
      editingPackage.resort_address,
      editingPackage.country,
      'resort hotel'
    ].filter(Boolean).join(' ');
    
    if (!searchTerms.trim()) {
      toast.error('Agrega el título o destino primero');
      return;
    }
    
    setSearchingImages(true);
    toast.loading('Buscando fotos automáticamente...', { id: 'auto-search' });
    
    try {
      // Search for images
      const response = await fetch(
        `${API}/certificates/simple-admin/search-images?q=${encodeURIComponent(searchTerms)}&count=6`,
        {
          headers: { 'X-Admin-Password': 'Csys250969' }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const newImages = (data.images || []).map(img => img.url);
        
        if (newImages.length > 0) {
          // Add all found images to the package
          const currentImages = (editingPackage.images || []).filter(img => img.trim());
          const uniqueNewImages = newImages.filter(url => !currentImages.includes(url));
          
          setEditingPackage({
            ...editingPackage,
            images: [...currentImages, ...uniqueNewImages]
          });
          
          toast.success(`¡${uniqueNewImages.length} fotos agregadas automáticamente!`, { id: 'auto-search' });
        } else {
          toast.error('No se encontraron fotos. Intenta con búsqueda manual.', { id: 'auto-search' });
        }
      } else {
        toast.error('Error al buscar imágenes', { id: 'auto-search' });
      }
    } catch (error) {
      console.error('Auto-search error:', error);
      toast.error('Error al buscar imágenes', { id: 'auto-search' });
    } finally {
      setSearchingImages(false);
    }
  };

  // Extract images from URL
  const extractImagesFromUrl = async () => {
    if (!extractUrl.trim()) {
      toast.error('Ingresa una URL');
      return;
    }
    
    setExtractingImages(true);
    setExtractedImages([]);
    toast.loading('Extrayendo imágenes...', { id: 'extract' });
    
    try {
      const response = await fetch(`${API}/certificates/simple-admin/extract-images-from-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': 'Csys250969'
        },
        body: JSON.stringify({ url: extractUrl })
      });
      
      const data = await response.json();
      
      if (data.images && data.images.length > 0) {
        setExtractedImages(data.images);
        toast.success(`¡${data.count} imágenes encontradas!`, { id: 'extract' });
      } else if (data.message) {
        // Show the message from backend (blocked site, error, etc.)
        toast.error(data.message, { id: 'extract', duration: 6000 });
      } else if (data.detail) {
        toast.error(data.detail, { id: 'extract' });
      } else {
        toast.info('No se encontraron imágenes. Intenta con otra URL o pega URLs directas.', { id: 'extract', duration: 5000 });
      }
    } catch (error) {
      console.error('Extract error:', error);
      toast.error('Error de conexión. Verifica tu internet e intenta de nuevo.', { id: 'extract' });
    } finally {
      setExtractingImages(false);
    }
  };

  const addExtractedImage = (imageUrl) => {
    // Filter out empty strings from current images
    const currentImages = (editingPackage.images || []).filter(img => img && img.trim() !== '');
    if (!currentImages.includes(imageUrl)) {
      setEditingPackage({
        ...editingPackage,
        images: [...currentImages, imageUrl]
      });
      toast.success('Imagen agregada');
    } else {
      toast.info('Esta imagen ya está agregada');
    }
  };

  const addAllExtractedImages = () => {
    // Filter out empty strings from current images
    const currentImages = (editingPackage.images || []).filter(img => img && img.trim() !== '');
    const newImages = extractedImages.map(img => img.url).filter(url => !currentImages.includes(url));
    
    if (newImages.length > 0) {
      setEditingPackage({
        ...editingPackage,
        images: [...currentImages, ...newImages]
      });
      toast.success(`¡${newImages.length} imágenes agregadas!`);
      setShowExtractModal(false);
      setExtractedImages([]);
      setExtractUrl('');
    } else {
      toast.info('Todas las imágenes ya están agregadas');
    }
  };

  const addImageFromSearch = (imageUrl) => {
    // Filter out empty strings from current images
    const currentImages = (editingPackage.images || []).filter(img => img && img.trim() !== '');
    if (!currentImages.includes(imageUrl)) {
      setEditingPackage({
        ...editingPackage,
        images: [...currentImages, imageUrl]
      });
      toast.success('Imagen agregada');
    } else {
      toast.info('Esta imagen ya está agregada');
    }
  };

  const addManualImage = () => {
    if (!manualImageUrl.trim()) return;
    
    // Filter out empty strings from current images
    const currentImages = (editingPackage.images || []).filter(img => img && img.trim() !== '');
    if (!currentImages.includes(manualImageUrl)) {
      setEditingPackage({
        ...editingPackage,
        images: [...currentImages, manualImageUrl]
      });
      setManualImageUrl('');
      toast.success('Imagen agregada');
    } else {
      toast.info('Esta imagen ya está agregada');
    }
  };

  const removeImage = (index) => {
    const currentImages = [...(editingPackage.images || [])];
    currentImages.splice(index, 1);
    setEditingPackage({ ...editingPackage, images: currentImages });
  };

  const moveImage = (index, direction) => {
    const currentImages = [...(editingPackage.images || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= currentImages.length) return;
    
    [currentImages[index], currentImages[newIndex]] = [currentImages[newIndex], currentImages[index]];
    setEditingPackage({ ...editingPackage, images: currentImages });
  };

  const fetchPackages = async () => {
    try {
      // Fetch all packages including paused ones
      const response = await fetch(`${API}/certificates?status=all`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Error al cargar los paquetes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingPackage({
      title: '',
      destination: '',
      description: '',
      days: 5,
      nights: 4,
      guests: 2,
      retail_price: 0,
      comparison_booking: 0,
      comparison_expedia: 0,
      comparison_despegar: 0,
      rating: 4.8,
      reviews_count: 50,
      images: [''],
      inclusions: ['Alojamiento', 'Desayuno', 'Traslados'],
      exclusions: ['Vuelos', 'Propinas'],
      conditions: '',
      presentation_info: '',
      stripe_link: '',
      status: 'active',
      // Nuevos campos
      country: '',
      date_type: 'open', // open, restricted, exact
      date_restrictions: '',
      available_dates: [{ date_range: '', characteristics: '' }], // Lista de fechas disponibles
      max_persons: 2,
      additional_text: '',
      // Ubicación del resort
      resort_address: '',
      latitude: '',
      longitude: ''
    });
    setShowEditor(true);
  };

  const handleEdit = (pkg) => {
    setEditingPackage({
      ...pkg,
      images: pkg.images?.length > 0 ? pkg.images : [''],
      inclusions: pkg.inclusions?.length > 0 ? pkg.inclusions : [''],
      exclusions: pkg.exclusions?.length > 0 ? pkg.exclusions : [''],
      available_dates: pkg.available_dates?.length > 0 ? pkg.available_dates : [{ date_range: '', characteristics: '' }],
      country: pkg.country || '',
      resort_address: pkg.resort_address || '',
      latitude: pkg.latitude || '',
      longitude: pkg.longitude || ''
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!editingPackage.title || !editingPackage.destination) {
      toast.error('Título y destino son requeridos');
      return;
    }

    setSaving(true);
    try {
      const isNew = !editingPackage.certificate_id;
      const url = isNew 
        ? `${API}/certificates/simple-admin/create`
        : `${API}/certificates/simple-admin/update/${editingPackage.certificate_id}`;
      
      // Clean up the data
      const packageData = {
        ...editingPackage,
        images: editingPackage.images.filter(img => img && img.trim() !== ''),
        inclusions: editingPackage.inclusions.filter(inc => inc.trim() !== ''),
        exclusions: editingPackage.exclusions.filter(exc => exc.trim() !== ''),
        available_dates: editingPackage.available_dates?.filter(d => d.date_range?.trim() !== '') || [],
        retail_price: parseFloat(editingPackage.retail_price) || 0,
        comparison_booking: parseFloat(editingPackage.comparison_booking) || 0,
        comparison_expedia: parseFloat(editingPackage.comparison_expedia) || 0,
        comparison_despegar: parseFloat(editingPackage.comparison_despegar) || 0,
        rating: parseFloat(editingPackage.rating) || 4.8,
        reviews_count: parseInt(editingPackage.reviews_count) || 50,
        days: parseInt(editingPackage.days) || 5,
        nights: parseInt(editingPackage.nights) || 4,
        guests: parseInt(editingPackage.guests) || 2,
        max_persons: parseInt(editingPackage.max_persons) || 2,
        terms_conditions: editingPackage.conditions || '',
        country: editingPackage.country || '',
        date_type: editingPackage.date_type || 'open',
        date_restrictions: editingPackage.date_restrictions || '',
        additional_text: editingPackage.additional_text || '',
        resort_address: editingPackage.resort_address || '',
        latitude: editingPackage.latitude ? parseFloat(editingPackage.latitude) : null,
        longitude: editingPackage.longitude ? parseFloat(editingPackage.longitude) : null
      };

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Password': 'Csys250969'
        },
        body: JSON.stringify(packageData)
      });

      if (response.ok) {
        toast.success(isNew ? 'Paquete creado exitosamente' : 'Paquete actualizado');
        setShowEditor(false);
        setEditingPackage(null);
        fetchPackages();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error('Error al guardar el paquete');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (certId) => {
    if (!confirm('¿Estás seguro de eliminar este paquete?')) return;

    try {
      const response = await fetch(`${API}/certificates/simple-admin/delete/${certId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': 'Csys250969' }
      });

      if (response.ok) {
        toast.success('Paquete eliminado');
        fetchPackages();
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error al eliminar el paquete');
    }
  };

  const toggleStatus = async (pkg) => {
    const newStatus = pkg.status === 'active' ? 'paused' : 'active';
    try {
      const response = await fetch(`${API}/certificates/simple-admin/update/${pkg.certificate_id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Password': 'Csys250969'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(newStatus === 'active' ? 'Paquete activado' : 'Paquete pausado');
        fetchPackages();
      }
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  // Admin Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Panel de Administración</h1>
            <p className="text-slate-500 mt-2">Ingresa tu contraseña para continuar</p>
          </div>

          <div className="space-y-4">
            <div>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                placeholder="Contraseña de administrador"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              {authError && (
                <p className="text-red-500 text-sm mt-2">{authError}</p>
              )}
            </div>
            <Button
              onClick={handleAdminLogin}
              className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white py-3 rounded-xl"
            >
              Ingresar
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="w-full text-slate-500"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Volver al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-container">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Inicio
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <Settings className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-slate-900">Admin Panel</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-500"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex items-center gap-4 mb-6 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('packages')}
            className={`pb-3 px-1 font-medium transition-colors relative ${
              activeTab === 'packages' 
                ? 'text-teal-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Paquetes
              <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{packages.length}</span>
            </div>
            {activeTab === 'packages' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
            )}
          </button>
          <button
            onClick={() => {
              setActiveTab('reservations');
              fetchReservations();
            }}
            className={`pb-3 px-1 font-medium transition-colors relative ${
              activeTab === 'reservations' 
                ? 'text-teal-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Reservaciones
              {reservations.length > 0 && (
                <span className="bg-teal-100 text-teal-600 text-xs px-2 py-0.5 rounded-full">{reservations.length}</span>
              )}
            </div>
            {activeTab === 'reservations' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
            )}
          </button>
          
          {/* Events Tab */}
          <button
            data-testid="events-tab"
            onClick={() => {
              setActiveTab('events');
              fetchEvents();
            }}
            className={`pb-3 px-1 font-medium transition-colors relative ${
              activeTab === 'events' 
                ? 'text-teal-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Eventos
              {events.filter(e => e.status === 'live').length > 0 && (
                <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full animate-pulse">
                  🔴 {events.filter(e => e.status === 'live').length}
                </span>
              )}
            </div>
            {activeTab === 'events' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
            )}
          </button>
        </div>

        {/* Packages Tab Content */}
        {activeTab === 'packages' && (
          <>
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Gestión de Paquetes</h1>
            <p className="text-slate-500 mt-1">Administra los certificados de viaje</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Import/Export Buttons */}
            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="border-slate-300 text-slate-700"
              data-testid="download-csv-template-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Plantilla CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowImportModal(true)}
              className="border-teal-500 text-teal-600 hover:bg-teal-50"
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importar CSV
            </Button>
            <Button
              onClick={handleCreateNew}
              className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
              data-testid="admin-add-package-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Paquete
            </Button>
          </div>
        </div>

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">Importar Paquetes</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportResult(null);
                  }}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Instructions */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-slate-900 mb-2">📋 Instrucciones</h3>
                <ol className="text-sm text-slate-600 space-y-2">
                  <li>1. <button onClick={downloadTemplate} className="text-teal-600 underline hover:text-teal-700">Descarga la plantilla CSV</button></li>
                  <li>2. Abre el archivo en Excel o Google Sheets</li>
                  <li>3. Llena tus paquetes (uno por fila)</li>
                  <li>4. Guarda como CSV (UTF-8)</li>
                  <li>5. Sube el archivo aquí</li>
                </ol>
              </div>

              {/* File Input */}
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                ref={fileInputRef}
                className="hidden"
                data-testid="csv-file-input"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white py-3 mb-4"
                data-testid="select-csv-file-btn"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Seleccionar archivo CSV
                  </>
                )}
              </Button>

              {/* Import Result */}
              {importResult && (
                <div className={`rounded-xl p-4 ${importResult.created > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {importResult.created > 0 && (
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <span className="text-2xl">✅</span>
                      <span className="font-semibold">{importResult.created} paquetes importados</span>
                    </div>
                  )}
                  {importResult.total_errors > 0 && (
                    <div className="text-sm text-red-600">
                      <p className="font-medium mb-1">⚠️ {importResult.total_errors} errores:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {importResult.errors?.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {importResult.total_errors > 5 && (
                          <li>... y {importResult.total_errors - 5} más</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* CSV Format Help */}
              <div className="mt-4 text-xs text-slate-500">
                <p className="font-medium mb-1">Columnas soportadas:</p>
                <p className="leading-relaxed">
                  titulo, destino, pais, descripcion, dias, noches, huespedes, precio_bombazoo, 
                  precio_booking, precio_expedia, precio_despegar, rating, reviews, imagenes (separadas por |), 
                  incluye (separado por |), no_incluye, tipo_fecha, fecha_disponible, caracteristicas, 
                  direccion_resort, latitud, longitud, estado
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Paquetes', value: packages.length, icon: Package },
            { label: 'Activos', value: packages.filter(p => p.status === 'active').length, icon: Eye },
            { label: 'Pausados', value: packages.filter(p => p.status !== 'active').length, icon: EyeOff },
            { label: 'Destinos', value: new Set(packages.map(p => p.destination)).size, icon: MapPin }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Packages List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : packages.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No hay paquetes</h3>
            <p className="text-slate-500 mb-6">Crea tu primer paquete de viaje</p>
            <Button onClick={handleCreateNew}>
              <Plus className="w-4 h-4 mr-2" />
              Crear Paquete
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {packages.map((pkg) => (
              <div
                key={pkg.certificate_id}
                className={`bg-white rounded-xl border ${pkg.status === 'active' ? 'border-slate-200' : 'border-orange-200 bg-orange-50/30'} p-4 flex flex-col md:flex-row md:items-center gap-4`}
              >
                {/* Image */}
                <div className="w-full md:w-32 h-24 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                  <img
                    src={pkg.images?.[0] || 'https://images.unsplash.com/photo-1693934304978-22274e6a3276?w=200&q=80'}
                    alt={pkg.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900 truncate">{pkg.title}</h3>
                        {pkg.status !== 'active' && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                            Pausado
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {pkg.destination}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {pkg.days}d/{pkg.nights}n
                        </span>
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-400" />
                          {pkg.rating || 4.8}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Prices */}
                  <div className="flex items-center gap-4 mt-2">
                    <div>
                      <span className="text-xs text-slate-400">Booking</span>
                      <p className="text-slate-400 line-through">${(pkg.comparison_booking || pkg.retail_price * 1.8).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-xs text-teal-600 font-medium">Bombazoo</span>
                      <p className="text-lg font-bold text-teal-600">${pkg.retail_price?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleStatus(pkg)}
                    className={pkg.status === 'active' ? 'text-orange-600 hover:bg-orange-50' : 'text-emerald-600 hover:bg-emerald-50'}
                  >
                    {pkg.status === 'active' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(pkg)}
                    data-testid={`admin-edit-package-button-${pkg.certificate_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(pkg.certificate_id)}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}

        {/* Reservations Tab Content */}
        {activeTab === 'reservations' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Reservaciones</h1>
                <p className="text-slate-500 mt-1">Gestiona las reservaciones de clientes</p>
              </div>
              <Button
                onClick={fetchReservations}
                variant="outline"
                className="border-slate-300"
              >
                🔄 Actualizar
              </Button>
            </div>

            {loadingReservations ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : reservations.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Sin reservaciones</h3>
                <p className="text-slate-500">Las reservaciones de clientes aparecerán aquí</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Paquete</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Contacto</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Viaje</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Agente</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Estado</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {reservations.map((res) => (
                        <tr key={res.reservation_id} className="hover:bg-slate-50">
                          <td className="px-4 py-4">
                            <div className="font-medium text-slate-900">{res.first_name} {res.last_name}</div>
                            <div className="text-sm text-slate-500">{res.address || '-'}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-slate-900 max-w-[200px] truncate">
                              {res.certificate_title || res.certificate_id}
                            </div>
                            <div className="text-xs text-slate-500">{res.num_persons} persona(s)</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1 text-sm text-slate-600">
                              <Mail className="w-3 h-3" />
                              <a href={`mailto:${res.email}`} className="hover:text-teal-600">{res.email}</a>
                            </div>
                            <div className="flex items-center gap-1 text-sm text-slate-600 mt-1">
                              <Phone className="w-3 h-3" />
                              <a href={`tel:${res.phone1}`} className="hover:text-teal-600">{res.phone1}</a>
                            </div>
                            {res.phone2 && (
                              <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                                <Phone className="w-3 h-3" />
                                <a href={`tel:${res.phone2}`} className="hover:text-teal-600">{res.phone2}</a>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-slate-600">
                              {res.travel_date ? new Date(res.travel_date).toLocaleDateString('es-ES', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              }) : 'Por definir'}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-slate-600">{res.agent_name || '-'}</div>
                          </td>
                          <td className="px-4 py-4">
                            <select
                              value={res.status}
                              onChange={(e) => updateReservationStatus(res.reservation_id, e.target.value)}
                              className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${
                                res.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                res.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                res.status === 'completed' ? 'bg-green-100 text-green-700' :
                                res.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-700'
                              }`}
                            >
                              <option value="pending">⏳ Pendiente</option>
                              <option value="confirmed">✅ Confirmada</option>
                              <option value="completed">🎉 Completada</option>
                              <option value="cancelled">❌ Cancelada</option>
                            </select>
                          </td>
                          <td className="px-4 py-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteReservation(res.reservation_id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Events Tab Content */}
        {activeTab === 'events' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Eventos / Enlaces en Vivo</h1>
                <p className="text-slate-500 mt-1">Gestiona los eventos y transmisiones en vivo</p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={fetchEvents}
                  variant="outline"
                  className="border-slate-300"
                >
                  🔄 Actualizar
                </Button>
                <Button
                  onClick={() => setShowEventModal(true)}
                  data-testid="new-event-btn"
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Evento
                </Button>
              </div>
            </div>

            {loadingEvents ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Sin eventos</h3>
                <p className="text-slate-500 mb-4">Crea tu primer evento o enlace en vivo</p>
                <Button
                  onClick={() => setShowEventModal(true)}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Evento
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {events.map((event) => (
                  <div
                    key={event.event_id}
                    className={`bg-white rounded-2xl border p-5 transition-all ${
                      event.status === 'live' 
                        ? 'border-red-300 shadow-lg shadow-red-100' 
                        : event.status === 'hidden'
                          ? 'border-slate-200 bg-slate-50'
                          : 'border-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg text-slate-900">{event.title}</h3>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            event.status === 'live' 
                              ? 'bg-red-100 text-red-700 animate-pulse' 
                              : event.status === 'hidden'
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-slate-100 text-slate-600'
                          }`}>
                            {event.status === 'live' && '🔴 EN VIVO'}
                            {event.status === 'hidden' && '👁️ OCULTO'}
                            {event.status === 'ended' && '⏹️ FINALIZADO'}
                          </span>
                        </div>
                        
                        {event.description && (
                          <p className="text-slate-600 text-sm mb-2">{event.description}</p>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                          {event.url && (
                            <a 
                              href={event.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-teal-600 hover:text-teal-700"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Ver enlace
                            </a>
                          )}
                          {event.scheduled_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {event.scheduled_date} {event.scheduled_time && `a las ${event.scheduled_time}`}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Action Buttons */}
                        {event.status !== 'live' && (
                          <Button
                            onClick={() => updateEventStatus(event.event_id, 'live')}
                            className="bg-red-500 hover:bg-red-600 text-white text-sm"
                            size="sm"
                            data-testid={`event-live-btn-${event.event_id}`}
                          >
                            🔴 EN VIVO
                          </Button>
                        )}
                        
                        {event.status !== 'hidden' && (
                          <Button
                            onClick={() => updateEventStatus(event.event_id, 'hidden')}
                            variant="outline"
                            className="border-slate-300 text-sm"
                            size="sm"
                            data-testid={`event-hide-btn-${event.event_id}`}
                          >
                            👁️ Ocultar
                          </Button>
                        )}
                        
                        <Button
                          data-testid={`event-delete-btn-${event.event_id}`}
                          onClick={() => deleteEvent(event.event_id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Nuevo Evento</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowEventModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Título *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  data-testid="event-title-input"
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="Ej: Transmisión en Vivo - Ofertas Especiales"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <textarea
                  value={newEvent.description}
                  data-testid="event-description-input"
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 h-20 resize-none"
                  placeholder="Descripción del evento..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">URL del Enlace</label>
                <input
                  type="url"
                  value={newEvent.url}
                  data-testid="event-url-input"
                  onChange={(e) => setNewEvent({...newEvent, url: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="https://..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fecha</label>
                  <input
                    type="date"
                    value={newEvent.scheduled_date}
                    onChange={(e) => setNewEvent({...newEvent, scheduled_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hora</label>
                  <input
                    type="time"
                    value={newEvent.scheduled_time}
                    onChange={(e) => setNewEvent({...newEvent, scheduled_time: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              
              <div className="pt-4 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowEventModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={createEvent} data-testid="event-create-submit-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
                  Crear Evento
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && editingPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl my-8">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-slate-900">
                {editingPackage.certificate_id ? 'Editar Paquete' : 'Nuevo Paquete'}
              </h2>
              <button
                onClick={() => { setShowEditor(false); setEditingPackage(null); }}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Título del Paquete *
                  </label>
                  <input
                    type="text"
                    value={editingPackage.title}
                    onChange={(e) => setEditingPackage({ ...editingPackage, title: e.target.value })}
                    placeholder="Ej: Escapada Romántica a Cancún"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Destino *
                  </label>
                  <input
                    type="text"
                    value={editingPackage.destination}
                    onChange={(e) => setEditingPackage({ ...editingPackage, destination: e.target.value })}
                    placeholder="Ej: Cancún, México"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    País *
                  </label>
                  <select
                    value={editingPackage.country || ''}
                    onChange={(e) => setEditingPackage({ ...editingPackage, country: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Seleccionar país</option>
                    <optgroup label="🌎 América del Norte">
                      <option value="México">🇲🇽 México</option>
                      <option value="Estados Unidos">🇺🇸 Estados Unidos</option>
                      <option value="Canadá">🇨🇦 Canadá</option>
                    </optgroup>
                    <optgroup label="🏝️ Caribe">
                      <option value="República Dominicana">🇩🇴 República Dominicana</option>
                      <option value="Cuba">🇨🇺 Cuba</option>
                      <option value="Puerto Rico">🇵🇷 Puerto Rico</option>
                      <option value="Jamaica">🇯🇲 Jamaica</option>
                      <option value="Bahamas">🇧🇸 Bahamas</option>
                      <option value="Aruba">🇦🇼 Aruba</option>
                      <option value="Curazao">🇨🇼 Curazao</option>
                      <option value="Barbados">🇧🇧 Barbados</option>
                      <option value="Trinidad y Tobago">🇹🇹 Trinidad y Tobago</option>
                      <option value="San Martín">🇸🇽 San Martín</option>
                      <option value="Islas Caimán">🇰🇾 Islas Caimán</option>
                      <option value="Islas Vírgenes">🇻🇬 Islas Vírgenes</option>
                      <option value="Turks y Caicos">🇹🇨 Turks y Caicos</option>
                      <option value="Antigua y Barbuda">🇦🇬 Antigua y Barbuda</option>
                      <option value="Santa Lucía">🇱🇨 Santa Lucía</option>
                      <option value="Granada">🇬🇩 Granada</option>
                      <option value="San Vicente">🇻🇨 San Vicente</option>
                    </optgroup>
                    <optgroup label="🌴 América Central">
                      <option value="Costa Rica">🇨🇷 Costa Rica</option>
                      <option value="Panamá">🇵🇦 Panamá</option>
                      <option value="Guatemala">🇬🇹 Guatemala</option>
                      <option value="Belice">🇧🇿 Belice</option>
                      <option value="Honduras">🇭🇳 Honduras</option>
                      <option value="Nicaragua">🇳🇮 Nicaragua</option>
                      <option value="El Salvador">🇸🇻 El Salvador</option>
                    </optgroup>
                    <optgroup label="🌎 América del Sur">
                      <option value="Colombia">🇨🇴 Colombia</option>
                      <option value="Perú">🇵🇪 Perú</option>
                      <option value="Brasil">🇧🇷 Brasil</option>
                      <option value="Argentina">🇦🇷 Argentina</option>
                      <option value="Chile">🇨🇱 Chile</option>
                      <option value="Ecuador">🇪🇨 Ecuador</option>
                      <option value="Uruguay">🇺🇾 Uruguay</option>
                      <option value="Venezuela">🇻🇪 Venezuela</option>
                      <option value="Bolivia">🇧🇴 Bolivia</option>
                      <option value="Paraguay">🇵🇾 Paraguay</option>
                    </optgroup>
                    <optgroup label="🏰 Europa Occidental">
                      <option value="España">🇪🇸 España</option>
                      <option value="Portugal">🇵🇹 Portugal</option>
                      <option value="Francia">🇫🇷 Francia</option>
                      <option value="Italia">🇮🇹 Italia</option>
                      <option value="Reino Unido">🇬🇧 Reino Unido</option>
                      <option value="Irlanda">🇮🇪 Irlanda</option>
                      <option value="Alemania">🇩🇪 Alemania</option>
                      <option value="Países Bajos">🇳🇱 Países Bajos</option>
                      <option value="Bélgica">🇧🇪 Bélgica</option>
                      <option value="Suiza">🇨🇭 Suiza</option>
                      <option value="Austria">🇦🇹 Austria</option>
                      <option value="Mónaco">🇲🇨 Mónaco</option>
                    </optgroup>
                    <optgroup label="🏛️ Mediterráneo">
                      <option value="Grecia">🇬🇷 Grecia</option>
                      <option value="Croacia">🇭🇷 Croacia</option>
                      <option value="Montenegro">🇲🇪 Montenegro</option>
                      <option value="Albania">🇦🇱 Albania</option>
                      <option value="Malta">🇲🇹 Malta</option>
                      <option value="Chipre">🇨🇾 Chipre</option>
                      <option value="Turquía">🇹🇷 Turquía</option>
                    </optgroup>
                    <optgroup label="❄️ Europa del Norte">
                      <option value="Noruega">🇳🇴 Noruega</option>
                      <option value="Suecia">🇸🇪 Suecia</option>
                      <option value="Dinamarca">🇩🇰 Dinamarca</option>
                      <option value="Finlandia">🇫🇮 Finlandia</option>
                      <option value="Islandia">🇮🇸 Islandia</option>
                    </optgroup>
                    <optgroup label="🏰 Europa del Este">
                      <option value="Polonia">🇵🇱 Polonia</option>
                      <option value="República Checa">🇨🇿 República Checa</option>
                      <option value="Hungría">🇭🇺 Hungría</option>
                      <option value="Rumania">🇷🇴 Rumania</option>
                      <option value="Bulgaria">🇧🇬 Bulgaria</option>
                    </optgroup>
                    <optgroup label="🌏 Asia del Sudeste">
                      <option value="Tailandia">🇹🇭 Tailandia</option>
                      <option value="Indonesia">🇮🇩 Indonesia</option>
                      <option value="Vietnam">🇻🇳 Vietnam</option>
                      <option value="Filipinas">🇵🇭 Filipinas</option>
                      <option value="Malasia">🇲🇾 Malasia</option>
                      <option value="Singapur">🇸🇬 Singapur</option>
                      <option value="Camboya">🇰🇭 Camboya</option>
                      <option value="Myanmar">🇲🇲 Myanmar</option>
                      <option value="Laos">🇱🇦 Laos</option>
                    </optgroup>
                    <optgroup label="🗾 Asia del Este">
                      <option value="Japón">🇯🇵 Japón</option>
                      <option value="Corea del Sur">🇰🇷 Corea del Sur</option>
                      <option value="China">🇨🇳 China</option>
                      <option value="Hong Kong">🇭🇰 Hong Kong</option>
                      <option value="Taiwán">🇹🇼 Taiwán</option>
                    </optgroup>
                    <optgroup label="🕌 Asia del Sur">
                      <option value="India">🇮🇳 India</option>
                      <option value="Sri Lanka">🇱🇰 Sri Lanka</option>
                      <option value="Nepal">🇳🇵 Nepal</option>
                      <option value="Maldivas">🇲🇻 Maldivas</option>
                      <option value="Bután">🇧🇹 Bután</option>
                    </optgroup>
                    <optgroup label="🕌 Medio Oriente">
                      <option value="Emiratos Árabes Unidos">🇦🇪 Emiratos Árabes Unidos</option>
                      <option value="Qatar">🇶🇦 Qatar</option>
                      <option value="Arabia Saudita">🇸🇦 Arabia Saudita</option>
                      <option value="Omán">🇴🇲 Omán</option>
                      <option value="Jordania">🇯🇴 Jordania</option>
                      <option value="Israel">🇮🇱 Israel</option>
                      <option value="Líbano">🇱🇧 Líbano</option>
                      <option value="Baréin">🇧🇭 Baréin</option>
                    </optgroup>
                    <optgroup label="🌍 África">
                      <option value="Marruecos">🇲🇦 Marruecos</option>
                      <option value="Egipto">🇪🇬 Egipto</option>
                      <option value="Sudáfrica">🇿🇦 Sudáfrica</option>
                      <option value="Kenia">🇰🇪 Kenia</option>
                      <option value="Tanzania">🇹🇿 Tanzania</option>
                      <option value="Mauricio">🇲🇺 Mauricio</option>
                      <option value="Seychelles">🇸🇨 Seychelles</option>
                      <option value="Madagascar">🇲🇬 Madagascar</option>
                      <option value="Túnez">🇹🇳 Túnez</option>
                      <option value="Cabo Verde">🇨🇻 Cabo Verde</option>
                      <option value="Zanzíbar">🇹🇿 Zanzíbar</option>
                      <option value="Namibia">🇳🇦 Namibia</option>
                      <option value="Botsuana">🇧🇼 Botsuana</option>
                      <option value="Ruanda">🇷🇼 Ruanda</option>
                    </optgroup>
                    <optgroup label="🏝️ Oceanía y Pacífico">
                      <option value="Australia">🇦🇺 Australia</option>
                      <option value="Nueva Zelanda">🇳🇿 Nueva Zelanda</option>
                      <option value="Fiyi">🇫🇯 Fiyi</option>
                      <option value="Polinesia Francesa">🇵🇫 Polinesia Francesa</option>
                      <option value="Hawái">🇺🇸 Hawái</option>
                      <option value="Samoa">🇼🇸 Samoa</option>
                      <option value="Tonga">🇹🇴 Tonga</option>
                      <option value="Vanuatu">🇻🇺 Vanuatu</option>
                      <option value="Nueva Caledonia">🇳🇨 Nueva Caledonia</option>
                      <option value="Islas Cook">🇨🇰 Islas Cook</option>
                      <option value="Bora Bora">🇵🇫 Bora Bora</option>
                      <option value="Tahití">🇵🇫 Tahití</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Resort Location */}
              <div className="bg-blue-50 rounded-xl p-4 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  Ubicación del Resort (para el mapa)
                </h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Dirección del Resort
                  </label>
                  <input
                    type="text"
                    value={editingPackage.resort_address || ''}
                    onChange={(e) => setEditingPackage({ ...editingPackage, resort_address: e.target.value })}
                    placeholder="Ej: Blvd Kukulcan Km 9.5, Zona Hotelera, Cancún"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Latitud
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={editingPackage.latitude || ''}
                      onChange={(e) => setEditingPackage({ ...editingPackage, latitude: e.target.value })}
                      placeholder="Ej: 21.1619"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Longitud
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={editingPackage.longitude || ''}
                      onChange={(e) => setEditingPackage({ ...editingPackage, longitude: e.target.value })}
                      placeholder="Ej: -86.8515"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  💡 Puedes obtener las coordenadas desde Google Maps: clic derecho en la ubicación → "¿Qué hay aquí?"
                </p>
              </div>

              {/* TripAdvisor Section */}
              <div className="bg-green-50 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#00aa6c">
                      <circle cx="12" cy="12" r="12"/>
                      <circle cx="8" cy="12" r="3" fill="white"/>
                      <circle cx="16" cy="12" r="3" fill="white"/>
                      <circle cx="8" cy="12" r="1.5" fill="#00aa6c"/>
                      <circle cx="16" cy="12" r="1.5" fill="#00aa6c"/>
                    </svg>
                    TripAdvisor
                  </h3>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      // Build search query
                      const searchTerms = [
                        editingPackage.title,
                        editingPackage.destination,
                        editingPackage.resort_address,
                        editingPackage.country
                      ].filter(Boolean).join(' ');
                      
                      if (!searchTerms.trim()) {
                        toast.error('Agrega el título o destino primero');
                        return;
                      }
                      
                      // Open TripAdvisor search in new tab
                      const searchUrl = `https://www.tripadvisor.com/Search?q=${encodeURIComponent(searchTerms)}`;
                      window.open(searchUrl, '_blank');
                      
                      toast.info('Busca tu hotel en TripAdvisor y copia:\n1. El link de la página\n2. El rating (ej: 4.5)', {
                        duration: 6000
                      });
                    }}
                    className="bg-[#00aa6c] hover:bg-[#009960] text-white"
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Buscar en TripAdvisor
                  </Button>
                </div>

                {/* Quick instructions */}
                <div className="bg-white/70 rounded-lg p-3 text-sm text-slate-600 space-y-1">
                  <p><strong>¿Cómo usar?</strong></p>
                  <ol className="list-decimal list-inside space-y-0.5 text-xs">
                    <li>Clic en "Buscar en TripAdvisor" → se abre una nueva pestaña</li>
                    <li>Encuentra tu hotel y abre su página</li>
                    <li>Copia el URL de la página (ej: tripadvisor.com/Hotel_Review-...)</li>
                    <li>Copia el rating (ej: 4.5 de 5 burbujas)</li>
                  </ol>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Link de TripAdvisor
                    </label>
                    <input
                      type="url"
                      value={editingPackage.tripadvisor_link || ''}
                      onChange={(e) => setEditingPackage({ ...editingPackage, tripadvisor_link: e.target.value })}
                      placeholder="Pega el link aquí..."
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Rating (1-5)
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="1"
                        max="5"
                        step="0.5"
                        value={editingPackage.tripadvisor_rating || ''}
                        onChange={(e) => setEditingPackage({ ...editingPackage, tripadvisor_rating: parseFloat(e.target.value) || '' })}
                        placeholder="4.5"
                        className="w-20 px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      />
                      {/* Preview of TripAdvisor circles */}
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`w-5 h-5 rounded-full border-2 cursor-pointer transition-all hover:scale-110 ${
                              editingPackage.tripadvisor_rating >= i 
                                ? 'bg-[#00aa6c] border-[#00aa6c]' 
                                : editingPackage.tripadvisor_rating >= i - 0.5
                                  ? 'bg-gradient-to-r from-[#00aa6c] from-50% to-white to-50% border-[#00aa6c]'
                                  : 'bg-white border-gray-300 hover:border-[#00aa6c]'
                            }`}
                            onClick={() => setEditingPackage({ ...editingPackage, tripadvisor_rating: i })}
                            title={`${i} estrellas`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview if link exists */}
                {editingPackage.tripadvisor_link && (
                  <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                    <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="#00aa6c">
                      <circle cx="12" cy="12" r="12"/>
                      <circle cx="8" cy="12" r="3" fill="white"/>
                      <circle cx="16" cy="12" r="3" fill="white"/>
                      <circle cx="8" cy="12" r="1.5" fill="#00aa6c"/>
                      <circle cx="16" cy="12" r="1.5" fill="#00aa6c"/>
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{editingPackage.tripadvisor_link}</p>
                      {editingPackage.tripadvisor_rating && (
                        <p className="text-xs text-slate-500">Rating: {editingPackage.tripadvisor_rating}/5</p>
                      )}
                    </div>
                    <a
                      href={editingPackage.tripadvisor_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00aa6c] hover:underline text-sm"
                    >
                      Ver ↗
                    </a>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={editingPackage.description}
                  onChange={(e) => setEditingPackage({ ...editingPackage, description: e.target.value })}
                  placeholder="Describe el paquete de viaje..."
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Duration & Guests */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Días</label>
                  <input
                    type="number"
                    value={editingPackage.days}
                    onChange={(e) => setEditingPackage({ ...editingPackage, days: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Noches</label>
                  <input
                    type="number"
                    value={editingPackage.nights}
                    onChange={(e) => setEditingPackage({ ...editingPackage, nights: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Huéspedes</label>
                  <input
                    type="number"
                    value={editingPackage.guests}
                    onChange={(e) => setEditingPackage({ ...editingPackage, guests: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Prices */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-teal-600" />
                  Precios (USD) - Comparación con Competencia
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Precio Bombazoo (venta)
                    </label>
                    <input
                      type="number"
                      value={editingPackage.retail_price}
                      onChange={(e) => setEditingPackage({ ...editingPackage, retail_price: e.target.value })}
                      placeholder="799"
                      className="w-full px-4 py-2.5 rounded-xl border border-teal-300 bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-500 font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Precio Booking.com
                    </label>
                    <input
                      type="number"
                      value={editingPackage.comparison_booking}
                      onChange={(e) => setEditingPackage({ ...editingPackage, comparison_booking: e.target.value })}
                      placeholder="1500"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Precio Expedia
                    </label>
                    <input
                      type="number"
                      value={editingPackage.comparison_expedia || ''}
                      onChange={(e) => setEditingPackage({ ...editingPackage, comparison_expedia: e.target.value })}
                      placeholder="1400"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Precio Despegar
                    </label>
                    <input
                      type="number"
                      value={editingPackage.comparison_despegar || ''}
                      onChange={(e) => setEditingPackage({ ...editingPackage, comparison_despegar: e.target.value })}
                      placeholder="1350"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                {editingPackage.comparison_booking > 0 && editingPackage.retail_price > 0 && (
                  <div className="bg-emerald-100 rounded-lg p-3 text-center">
                    <span className="text-emerald-700 font-semibold">
                      Ahorro vs Booking: ${(editingPackage.comparison_booking - editingPackage.retail_price).toLocaleString()} 
                      ({Math.round(((editingPackage.comparison_booking - editingPackage.retail_price) / editingPackage.comparison_booking) * 100)}%)
                    </span>
                  </div>
                )}
              </div>

              {/* Date Type & Persons */}
              <div className="bg-amber-50 rounded-xl p-4 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-amber-600" />
                  Disponibilidad y Capacidad
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Tipo de Fecha
                    </label>
                    <select
                      value={editingPackage.date_type || 'open'}
                      onChange={(e) => setEditingPackage({ ...editingPackage, date_type: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="open">🟢 ABIERTO - Fechas Flexibles</option>
                      <option value="restricted">🟡 FECHAS RESTRINGIDAS</option>
                      <option value="exact">🔴 FECHAS EXACTAS</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Máximo de Personas
                    </label>
                    <input
                      type="number"
                      value={editingPackage.max_persons || editingPackage.guests || 2}
                      onChange={(e) => setEditingPackage({ ...editingPackage, max_persons: e.target.value })}
                      min="1"
                      max="20"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                
                {editingPackage.date_type === 'restricted' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Restricciones de Fecha
                    </label>
                    <textarea
                      value={editingPackage.date_restrictions || ''}
                      onChange={(e) => setEditingPackage({ ...editingPackage, date_restrictions: e.target.value })}
                      placeholder="Ej: No válido en Semana Santa, Navidad, Año Nuevo..."
                      rows={2}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                )}
                
                {editingPackage.date_type === 'exact' && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-slate-700">
                      Fechas Disponibles (Certificados de Tiempo Compartido)
                    </label>
                    <p className="text-xs text-slate-500">
                      Agrega las fechas específicas emitidas por el dueño del tiempo compartido con sus características.
                    </p>
                    {(editingPackage.available_dates || [{ date_range: '', characteristics: '' }]).map((dateItem, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-amber-200 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-amber-700">Fecha #{idx + 1}</span>
                          {editingPackage.available_dates?.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newDates = editingPackage.available_dates.filter((_, i) => i !== idx);
                                setEditingPackage({ ...editingPackage, available_dates: newDates });
                              }}
                              className="text-red-500 h-6 px-2"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <input
                          type="text"
                          value={dateItem.date_range || ''}
                          onChange={(e) => {
                            const newDates = [...(editingPackage.available_dates || [])];
                            newDates[idx] = { ...newDates[idx], date_range: e.target.value };
                            setEditingPackage({ ...editingPackage, available_dates: newDates });
                          }}
                          placeholder="Ej: 15-20 Marzo 2025"
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                        />
                        <input
                          type="text"
                          value={dateItem.characteristics || ''}
                          onChange={(e) => {
                            const newDates = [...(editingPackage.available_dates || [])];
                            newDates[idx] = { ...newDates[idx], characteristics: e.target.value };
                            setEditingPackage({ ...editingPackage, available_dates: newDates });
                          }}
                          placeholder="Características: Vista al mar, Suite junior, Todo incluido..."
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                        />
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newDates = [...(editingPackage.available_dates || []), { date_range: '', characteristics: '' }];
                        setEditingPackage({ ...editingPackage, available_dates: newDates });
                      }}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar Otra Fecha
                    </Button>
                  </div>
                )}
              </div>

              {/* Additional Text */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Texto Adicional / Nota Especial
                </label>
                <textarea
                  value={editingPackage.additional_text || ''}
                  onChange={(e) => setEditingPackage({ ...editingPackage, additional_text: e.target.value })}
                  placeholder="Información adicional que quieras mostrar a los clientes..."
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Rating */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Calificación (1-5)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={editingPackage.rating}
                    onChange={(e) => setEditingPackage({ ...editingPackage, rating: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Número de Opiniones
                  </label>
                  <input
                    type="number"
                    value={editingPackage.reviews_count}
                    onChange={(e) => setEditingPackage({ ...editingPackage, reviews_count: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Images - New Enhanced Section */}
              <div className="bg-purple-50 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-purple-600" />
                    Imágenes del Paquete
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => setShowExtractModal(true)}
                      className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Extraer de URL
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={autoCompleteImages}
                      disabled={searchingImages}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                    >
                      {searchingImages ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                      ) : (
                        <span className="mr-1">🪄</span>
                      )}
                      Auto-completar
                    </Button>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-white/70 rounded-lg p-3 text-sm text-slate-600 space-y-1">
                  <p><strong>🔗 Extraer de URL:</strong> Pega el link de la página del hotel (Booking, TripAdvisor, etc.) y el sistema descarga todas las fotos.</p>
                  <p><strong>🪄 Auto-completar:</strong> Busca fotos automáticamente usando el nombre y destino del resort.</p>
                </div>

                {/* Manual URL Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={manualImageUrl}
                    onChange={(e) => setManualImageUrl(e.target.value)}
                    placeholder="O pega una URL de imagen específica (https://...jpg)"
                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addManualImage()}
                  />
                  <Button
                    onClick={addManualImage}
                    disabled={!manualImageUrl.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Current Images Preview */}
                {editingPackage.images && editingPackage.images.filter(img => img.trim()).length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">{editingPackage.images.filter(img => img.trim()).length} imagen(es) seleccionada(s)</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {editingPackage.images.filter(img => img.trim()).map((img, idx) => (
                        <div key={idx} className="relative group rounded-xl overflow-hidden border-2 border-slate-200 bg-white">
                          <img
                            src={img}
                            alt={`Imagen ${idx + 1}`}
                            className="w-full h-24 object-cover"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/200x150?text=Error';
                            }}
                          />
                          {/* Image order badge */}
                          <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-2 py-0.5 rounded-full">
                            {idx + 1}
                          </div>
                          {/* Controls overlay */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                            {idx > 0 && (
                              <button
                                onClick={() => moveImage(idx, -1)}
                                className="p-1.5 bg-white rounded-full hover:bg-slate-100"
                                title="Mover arriba"
                              >
                                <ArrowUp className="w-4 h-4 text-slate-700" />
                              </button>
                            )}
                            {idx < editingPackage.images.filter(img => img.trim()).length - 1 && (
                              <button
                                onClick={() => moveImage(idx, 1)}
                                className="p-1.5 bg-white rounded-full hover:bg-slate-100"
                                title="Mover abajo"
                              >
                                <ArrowDown className="w-4 h-4 text-slate-700" />
                              </button>
                            )}
                            <button
                              onClick={() => removeImage(idx)}
                              className="p-1.5 bg-red-500 rounded-full hover:bg-red-600"
                              title="Eliminar"
                            >
                              <X className="w-4 h-4 text-white" />
                            </button>
                            <a
                              href={img}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 bg-white rounded-full hover:bg-slate-100"
                              title="Ver original"
                            >
                              <ExternalLink className="w-4 h-4 text-slate-700" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 bg-white rounded-xl border-2 border-dashed border-slate-200">
                    <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">No hay imágenes agregadas</p>
                    <p className="text-slate-400 text-xs">Busca imágenes o pega URLs arriba</p>
                  </div>
                )}
              </div>

              {/* Image Search Modal */}
              {showImageSearch && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                  <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
                    <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <Search className="w-5 h-5 text-purple-600" />
                        Buscar Imágenes
                      </h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowImageSearch(false)}>
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    <div className="p-4 border-b border-slate-100">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={imageSearchQuery}
                          onChange={(e) => setImageSearchQuery(e.target.value)}
                          placeholder="Buscar: Cancún beach resort, luxury hotel pool..."
                          className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          onKeyPress={(e) => e.key === 'Enter' && searchImages()}
                        />
                        <Button
                          onClick={searchImages}
                          disabled={searchingImages || !imageSearchQuery.trim()}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                        >
                          {searchingImages ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Search className="w-5 h-5" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        💡 Tip: Busca en inglés para mejores resultados (ej: "beach resort cancun", "luxury pool hotel")
                      </p>
                    </div>
                    
                    <div className="p-4 overflow-y-auto max-h-[50vh]">
                      {searchResults.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {searchResults.map((img) => (
                            <div
                              key={img.id}
                              className="relative group cursor-pointer rounded-xl overflow-hidden border-2 border-slate-200 hover:border-purple-500 transition-colors"
                              onClick={() => addImageFromSearch(img.url)}
                            >
                              <img
                                src={img.thumb || img.url}
                                alt={img.alt || 'Search result'}
                                className="w-full h-28 object-cover"
                              />
                              <div className="absolute inset-0 bg-purple-600/0 group-hover:bg-purple-600/30 transition-colors flex items-center justify-center">
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-2 shadow-lg">
                                  <Plus className="w-5 h-5 text-purple-600" />
                                </div>
                              </div>
                              {img.photographer && (
                                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-2 py-1 truncate">
                                  📷 {img.photographer}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : searchingImages ? (
                        <div className="text-center py-12">
                          <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                          <p className="text-slate-500">Buscando imágenes...</p>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500">Escribe un término y presiona buscar</p>
                          <p className="text-slate-400 text-sm mt-1">Las imágenes son de Unsplash (uso gratuito)</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
                      <p className="text-sm text-slate-600">
                        {editingPackage.images?.filter(img => img.trim()).length || 0} imagen(es) seleccionada(s)
                      </p>
                      <Button onClick={() => setShowImageSearch(false)} className="bg-slate-900 hover:bg-slate-800 text-white">
                        Listo
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Extract Images from URL Modal */}
              {showExtractModal && (
                <div data-testid="extract-modal" className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                  <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
                    <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-cyan-50">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <ExternalLink className="w-5 h-5 text-blue-600" />
                        Extraer Fotos de URL
                      </h3>
                      <Button data-testid="extract-close" variant="ghost" size="sm" onClick={() => {
                        setShowExtractModal(false);
                        setExtractedImages([]);
                        setExtractUrl('');
                      }}>
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    <div className="p-4 border-b border-slate-100 space-y-4">
                      {/* Option 1: Extract from webpage */}
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">
                          🌐 Opción 1: Extraer de una página web
                        </p>
                        <div className="flex gap-2">
                          <input
                            data-testid="extract-url-input"
                            type="url"
                            value={extractUrl}
                            onChange={(e) => setExtractUrl(e.target.value)}
                            placeholder="https://www.hotel-ejemplo.com/galeria"
                            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            onKeyPress={(e) => e.key === 'Enter' && extractImagesFromUrl()}
                          />
                          <Button
                            data-testid="extract-url-submit"
                            onClick={extractImagesFromUrl}
                            disabled={extractingImages || !extractUrl.trim()}
                            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-4"
                          >
                            {extractingImages ? (
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          ⚠️ Algunos sitios (Booking, TripAdvisor) bloquean la extracción. Usa la Opción 2 si no funciona.
                        </p>
                      </div>
                      
                      {/* Divider */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-slate-200"></div>
                        <span className="text-xs text-slate-400">o</span>
                        <div className="flex-1 h-px bg-slate-200"></div>
                      </div>
                      
                      {/* Option 2: Paste multiple URLs */}
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">
                          📋 Opción 2: Pegar URLs de imágenes (una por línea)
                        </p>
                        <textarea
                          data-testid="extract-paste-textarea"
                          placeholder="Pega aquí los links de las imágenes, uno por línea:&#10;https://ejemplo.com/foto1.jpg&#10;https://ejemplo.com/foto2.jpg&#10;https://ejemplo.com/foto3.jpg"
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm h-24 resize-none"
                          onChange={(e) => {
                            const urls = e.target.value.split('\n')
                              .map(url => url.trim())
                              .filter(url => url.startsWith('http'));
                            if (urls.length > 0) {
                              setExtractedImages(urls.map((url, i) => ({ url, alt: `Imagen ${i+1}` })));
                            }
                          }}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          💡 Copia las URLs de imágenes desde cualquier sitio web (clic derecho → "Copiar dirección de imagen")
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-4 overflow-y-auto max-h-[50vh]">
                      {extractedImages.length > 0 ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-slate-700">
                              {extractedImages.length} imágenes encontradas
                            </p>
                            <Button
                              data-testid="extract-add-all"
                              size="sm"
                              onClick={addAllExtractedImages}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Agregar todas
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {extractedImages.map((img, idx) => (
                              <div
                                key={idx}
                                data-testid={`extract-image-tile-${idx}`}
                                className="relative group cursor-pointer rounded-xl overflow-hidden border-2 border-slate-200 hover:border-blue-500 transition-colors"
                                onClick={() => addExtractedImage(img.url)}
                              >
                                <img
                                  src={img.url}
                                  alt={img.alt || `Imagen ${idx + 1}`}
                                  className="w-full h-28 object-cover"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/200x150?text=Error';
                                  }}
                                />
                                <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/30 transition-colors flex items-center justify-center">
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-2 shadow-lg">
                                    <Plus className="w-5 h-5 text-blue-600" />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : extractingImages ? (
                        <div className="text-center py-12">
                          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                          <p className="text-slate-500">Extrayendo imágenes de la página...</p>
                          <p className="text-slate-400 text-xs mt-1">Esto puede tomar unos segundos</p>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <ExternalLink className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500">Pega una URL y presiona "Extraer"</p>
                          <p className="text-slate-400 text-sm mt-1">Funciona con Booking, TripAdvisor, páginas de hoteles, etc.</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
                      <p className="text-sm text-slate-600">
                        {editingPackage.images?.filter(img => img.trim()).length || 0} imagen(es) en el paquete
                      </p>
                      <Button onClick={() => {
                        setShowExtractModal(false);
                        setExtractedImages([]);
                        setExtractUrl('');
                      }} className="bg-slate-900 hover:bg-slate-800 text-white">
                        Cerrar
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Inclusions */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  ¿Qué Incluye?
                </label>
                {editingPackage.inclusions.map((inc, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={inc}
                      onChange={(e) => {
                        const newInclusions = [...editingPackage.inclusions];
                        newInclusions[idx] = e.target.value;
                        setEditingPackage({ ...editingPackage, inclusions: newInclusions });
                      }}
                      placeholder="Ej: Desayuno buffet"
                      className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    {editingPackage.inclusions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newInclusions = editingPackage.inclusions.filter((_, i) => i !== idx);
                          setEditingPackage({ ...editingPackage, inclusions: newInclusions });
                        }}
                        className="text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingPackage({ ...editingPackage, inclusions: [...editingPackage.inclusions, ''] })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>

              {/* Exclusions */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  No Incluye
                </label>
                {editingPackage.exclusions.map((exc, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={exc}
                      onChange={(e) => {
                        const newExclusions = [...editingPackage.exclusions];
                        newExclusions[idx] = e.target.value;
                        setEditingPackage({ ...editingPackage, exclusions: newExclusions });
                      }}
                      placeholder="Ej: Vuelos"
                      className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    {editingPackage.exclusions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newExclusions = editingPackage.exclusions.filter((_, i) => i !== idx);
                          setEditingPackage({ ...editingPackage, exclusions: newExclusions });
                        }}
                        className="text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingPackage({ ...editingPackage, exclusions: [...editingPackage.exclusions, ''] })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>

              {/* Conditions */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Información Importante / Condiciones
                </label>
                <textarea
                  value={editingPackage.conditions}
                  onChange={(e) => setEditingPackage({ ...editingPackage, conditions: e.target.value })}
                  placeholder="Información sobre la presentación de timeshare, condiciones, etc."
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Stripe Link */}
              <div className="bg-blue-50 rounded-xl p-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Link de Pago Stripe (opcional)
                </label>
                <input
                  type="text"
                  value={editingPackage.stripe_link || ''}
                  onChange={(e) => setEditingPackage({ ...editingPackage, stripe_link: e.target.value })}
                  placeholder="https://buy.stripe.com/xxx"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Si se proporciona, el cliente será redirigido a este link al hacer clic en "Reservar"
                </p>
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-slate-700">Estado:</label>
                <select
                  value={editingPackage.status}
                  onChange={(e) => setEditingPackage({ ...editingPackage, status: e.target.value })}
                  className="px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="active">Activo (Visible)</option>
                  <option value="paused">Pausado (Oculto)</option>
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowEditor(false); setEditingPackage(null); }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                data-testid="admin-save-package-button"
              >
                {saving ? 'Guardando...' : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Paquete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
